// SetupBanner — Phase 3b. "Apply" commits `.loom.yml`; "Customise"
// expands an inline per-stage chip editor bound to `mapping[stage]`.
// Save calls the same PUT route. On `concurrent_write` 409 we keep the
// in-form edits and surface a refresh prompt — the customer's work is
// not lost.

import {
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import {
  DASHBOARD_STAGES,
  type ApplyMappingPayload,
  type DashboardStage,
} from "@loom/shared/dashboard";

import { useT, type MessageKey, type Translator } from "../../i18n/index.js";
import type { DashboardConfigResponse } from "../useDashboard";

import { Icon } from "./Icon";

interface SetupBannerProps {
  readonly config: DashboardConfigResponse;
  readonly saving: boolean;
  readonly error: string | null;
  /**
   * Full label vocabulary fetched from the project's GitLab labels API.
   * Surfaced to each ChipRow as autocomplete suggestions so the user
   * picks real labels instead of typing them in by hand and discovering
   * the casing was wrong only after Apply. Defaults to an empty list,
   * in which case the input falls back to a plain text field.
   */
  readonly availableLabels?: ReadonlyArray<string>;
  readonly onApply: (payload: ApplyMappingPayload) => Promise<void> | void;
  readonly onRefresh: () => void;
}

const STAGE_LABEL_KEY: Record<DashboardStage, MessageKey> = {
  needs_input: "dashboard.setup.stage.needs_input",
  planned: "dashboard.setup.stage.planned",
  in_progress: "dashboard.setup.stage.in_progress",
  review: "dashboard.setup.stage.review",
  shipped: "dashboard.setup.stage.shipped",
};

type StageMapping = ApplyMappingPayload["mapping"];

const emptyMapping = (): StageMapping => ({
  needs_input: [],
  planned: [],
  in_progress: [],
  review: [],
  shipped: [],
});

const seedFromSuggestion = (
  suggestion: DashboardConfigResponse["suggestion"],
): StageMapping => {
  const seed = emptyMapping();
  for (const stage of DASHBOARD_STAGES) {
    seed[stage] = [...(suggestion.mapping[stage] ?? [])];
  }
  return seed;
};

export const SetupBanner = ({
  config,
  saving,
  error,
  availableLabels = [],
  onApply,
  onRefresh,
}: SetupBannerProps): JSX.Element => {
  const t = useT();
  const { suggestion } = config;
  const [editing, setEditing] = useState(false);
  const [mapping, setMapping] = useState<StageMapping>(() =>
    seedFromSuggestion(suggestion),
  );
  const [decisionLabels, setDecisionLabels] = useState<string[]>(() => [
    ...suggestion.decisionLabels,
  ]);

  const rows = DASHBOARD_STAGES.map((stage) => ({
    stage,
    labels: mapping[stage] ?? [],
  })).filter((row) => editing || row.labels.length > 0);

  const setStageLabels = (stage: DashboardStage, next: string[]): void => {
    setMapping((cur) => ({ ...cur, [stage]: next }));
  };

  const apply = async (): Promise<void> => {
    await onApply({ mapping, decisionLabels });
  };

  const concurrentWrite = error === "concurrent_write";

  return (
    <div className="setup-banner">
      <Icon name="wand" size={18} />
      <div className="setup-banner-body">
        <div className="setup-banner-eyebrow">{t("dashboard.setup.eyebrow")}</div>
        <div className="setup-banner-title">
          {suggestion.chosenScope
            ? t("dashboard.setup.suggestedMapping", {
                scope: suggestion.chosenScope,
                confidence: suggestion.confidence,
              })
            : t("dashboard.setup.noMatchingLabels")}
        </div>
        {!editing && rows.length > 0 && (
          <dl className="setup-banner-list">
            {rows.map(({ stage, labels }) => (
              <div
                key={stage}
                style={{ display: "contents" }}
                data-stage={stage}
              >
                <dt>{t(STAGE_LABEL_KEY[stage])}</dt>
                <dd style={{ margin: 0 }}>{labels.join(", ")}</dd>
              </div>
            ))}
          </dl>
        )}
        {editing && (
          <div className="setup-banner-edit">
            {DASHBOARD_STAGES.map((stage) => (
              <ChipRow
                key={stage}
                label={t(STAGE_LABEL_KEY[stage])}
                dataKey={stage}
                values={mapping[stage] ?? []}
                suggestions={availableLabels}
                onChange={(next) => setStageLabels(stage, next)}
                t={t}
              />
            ))}
            <ChipRow
              label={t("dashboard.setup.decisionLabels")}
              dataKey="decision labels"
              values={decisionLabels}
              suggestions={availableLabels}
              onChange={setDecisionLabels}
              t={t}
            />
          </div>
        )}
        {error !== null && (
          <div className="setup-banner-error">
            {concurrentWrite
              ? t("dashboard.setup.concurrentWrite")
              : error}
          </div>
        )}
      </div>
      <div className="setup-banner-actions">
        {concurrentWrite && (
          <button
            type="button"
            className="btn-ghost-lg"
            onClick={onRefresh}
            disabled={saving}
          >
            {t("dashboard.setup.refresh")}
          </button>
        )}
        <button
          type="button"
          className="btn-ghost-lg"
          onClick={() => setEditing((on) => !on)}
          disabled={saving}
        >
          {editing ? t("dashboard.setup.hide") : t("dashboard.setup.customise")}
        </button>
        <button
          type="button"
          className="btn-ghost-lg"
          onClick={() => void apply()}
          disabled={saving}
        >
          {saving ? t("dashboard.setup.applying") : t("dashboard.setup.apply")}
        </button>
      </div>
    </div>
  );
};

interface ChipRowProps {
  readonly label: string;
  /** Stable, locale-independent identifier for the `data-stage-edit`
   *  automation hook — kept separate from the translated `label`. */
  readonly dataKey: string;
  readonly values: ReadonlyArray<string>;
  /**
   * Pool to match against for autocomplete suggestions. When non-empty
   * the input opens a popover with the top matches (case-insensitive
   * substring) and supports ↑/↓/Enter for keyboard selection. When empty
   * the input degrades to plain text + Enter/blur commit.
   */
  readonly suggestions?: ReadonlyArray<string>;
  readonly onChange: (next: string[]) => void;
  readonly t: Translator;
}

const MAX_SUGGESTIONS = 8;

const filterSuggestions = (
  pool: ReadonlyArray<string>,
  already: ReadonlyArray<string>,
  draft: string,
): string[] => {
  const taken = new Set(already);
  const needle = draft.trim().toLowerCase();
  const candidates = pool.filter((s) => !taken.has(s));
  if (needle.length === 0) return candidates.slice(0, MAX_SUGGESTIONS);
  // Rank: prefix matches first, then substring matches, both
  // case-insensitive. Tied items keep server-side sort order (which is
  // already alphabetical).
  const prefix: string[] = [];
  const substring: string[] = [];
  for (const cand of candidates) {
    const lc = cand.toLowerCase();
    if (lc.startsWith(needle)) prefix.push(cand);
    else if (lc.includes(needle)) substring.push(cand);
  }
  return [...prefix, ...substring].slice(0, MAX_SUGGESTIONS);
};

const ChipRow = ({
  label,
  dataKey,
  values,
  suggestions = [],
  onChange,
  t,
}: ChipRowProps): JSX.Element => {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<number | null>(null);
  const listboxId = useId();

  const matches = useMemo(
    () => filterSuggestions(suggestions, values, draft),
    [suggestions, values, draft],
  );
  const showPopover = focused && suggestions.length > 0 && matches.length > 0;

  const add = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return;
    if (values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
    setHighlight(0);
  };

  const commitDraft = (): void => {
    if (showPopover && matches[highlight] !== undefined) {
      add(matches[highlight]!);
      return;
    }
    add(draft);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === "Tab" && showPopover && matches[highlight] !== undefined) {
      e.preventDefault();
      add(matches[highlight]!);
      return;
    }
    if (e.key === "ArrowDown" && showPopover) {
      e.preventDefault();
      setHighlight((i) => Math.min(matches.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp" && showPopover) {
      e.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Escape") {
      setDraft("");
      setFocused(false);
      return;
    }
    if (e.key === "Backspace" && draft.length === 0 && values.length > 0) {
      // Quality-of-life: backspace on empty input drops the last chip,
      // matching how every other tag input on the web behaves.
      e.preventDefault();
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="setup-banner-edit-row" data-stage-edit={dataKey}>
      <span className="setup-banner-edit-label">{label}</span>
      <div className="setup-banner-edit-chips">
        {values.map((v) => (
          <span key={v} className="setup-banner-edit-chip">
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              aria-label={t("dashboard.setup.removeLabel", { label: v })}
            >
              <Icon name="x" size={10} />
            </button>
          </span>
        ))}
        <span className="setup-banner-edit-combo">
          <input
            className="setup-banner-edit-input"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            onFocus={() => {
              if (blurTimer.current !== null) {
                window.clearTimeout(blurTimer.current);
                blurTimer.current = null;
              }
              setFocused(true);
              setHighlight(0);
            }}
            onBlur={() => {
              // Delay so a click on a popover item registers before the
              // popover unmounts. The click handler clears the timer.
              blurTimer.current = window.setTimeout(() => {
                setFocused(false);
                add(draft);
              }, 120);
            }}
            placeholder={
              suggestions.length > 0
                ? t("dashboard.setup.searchLabels")
                : t("dashboard.setup.addLabel")
            }
            role={suggestions.length > 0 ? "combobox" : undefined}
            aria-autocomplete={suggestions.length > 0 ? "list" : undefined}
            aria-expanded={showPopover}
            aria-controls={showPopover ? listboxId : undefined}
            aria-activedescendant={
              showPopover && matches[highlight] !== undefined
                ? `${listboxId}-${highlight}`
                : undefined
            }
          />
          {showPopover && (
            <ul
              id={listboxId}
              role="listbox"
              className="setup-banner-edit-popover"
            >
              {matches.map((s, i) => (
                <li
                  key={s}
                  id={`${listboxId}-${i}`}
                  role="option"
                  aria-selected={i === highlight}
                  className={
                    i === highlight
                      ? "setup-banner-edit-suggestion on"
                      : "setup-banner-edit-suggestion"
                  }
                  onMouseDown={(e) => {
                    // mousedown beats blur — keeps focus and avoids the
                    // blur-timer race with the click.
                    e.preventDefault();
                    if (blurTimer.current !== null) {
                      window.clearTimeout(blurTimer.current);
                      blurTimer.current = null;
                    }
                    add(s);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                >
                  {s}
                </li>
              ))}
            </ul>
          )}
        </span>
      </div>
    </div>
  );
};
